import type { AutomergeUrl } from "@automerge/automerge-repo";
import { RepoContext, useDocument } from "@automerge/react";
import { queryOptions, useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  getRouteApi,
  Link,
  useNavigate,
} from "@tanstack/react-router";
import type { UIMessage } from "ai";
import type { TaskActivity } from "apprentices/task-activity";
import type { ShowInput } from "apprentices/tools/show";
import { chatCommands, mintId } from "den/client/use-chat-feed";
import type { SentMessage } from "forest/chat/types";
import { useCheckpointRail } from "forest/chat/use-checkpoint-rail";
import { useTreeEntries } from "forest/file-tree/use-tree";
import {
  ArrowLeft,
  Box,
  CircleDot,
  ChevronRight,
  GitBranch,
  GitMerge,
  BookOpen,
  ListChecks,
  Loader2,
  X,
} from "lucide-react";
import {
  textAnnotations,
  type AnnotatedDoc,
} from "playground/annotations/annotations";
import type { BranchChangePreview } from "playground/branches/changes";
import { forkBranchFile } from "playground/branches/project-branch";
import { isBranchedProject } from "playground/branches/project-branch";
import { resolveModelEntry } from "playground/builder/entry";
import { isMountInode } from "playground/fs/mount";
import { hasStats } from "playground/fs/types";
import type { HostPlayground } from "playground/hal/index";
import {
  executionSchema,
  type Execution,
  type TaskReview,
} from "playground/migrations/contract";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import { invariant } from "ts-invariant";

import { AskBearProvider, useAskBearDispatch } from "@/comments/ask-bear";
import { CommentsButton, FileComments } from "@/comments/FileComments";
import BearSandboxProvider, {
  useBearSandbox,
} from "@/components/BearSandboxProvider";
import { useProjectShellTasks } from "@/components/ProjectShell";
import { TaskChanges } from "@/components/TaskChanges";
import {
  failureCount,
  isCheckFile,
  TaskChecks,
  useTaskChecks,
} from "@/components/TaskChecks";
import { TaskContext } from "@/components/TaskContext";
import { MigrationReview, MigrationStatus } from "@/components/TaskMigration";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemTitle,
} from "@/components/ui/item";
import { WorkshopPrompt } from "@/components/WorkshopPrompt";
import { JigsProvider } from "@/jigs/JigsProvider";
import { blobSrc } from "@/lib/blob-src";
import { coalescedRefresh } from "@/lib/coalesced-refresh";
import { fetchTaskActivity, useTaskActivity } from "@/lib/task-activity";
import { taskReads, type TaskRead } from "@/lib/task-context";
import { cn } from "@/lib/utils";
import {
  canReviewTask,
  workshopTaskState,
  type TaskStatus,
  type WorkshopSection,
} from "@/lib/workshop-task-state";
import BearModelPane from "@/panes/BearModelPane";
import BuilderPane from "@/panes/BuilderPane";
import EditorPane from "@/panes/EditorPane";
import { useFollowConversation } from "@/panes/follow-conversation";
import {
  markupViewOf,
  useTreeVersion,
  type MarkupView,
  type ModelMarkupHost,
} from "@/panes/model-markup";
import PdfPane from "@/panes/PdfPane";
import SketchPane from "@/panes/SketchPane";
import { createChat, useSharedProjectChats } from "@/panes/use-project-chats";
import type { Project } from "@/types/projects";

import "./studio-theme.css";

interface TaskRow {
  id: string;
  project_id: string;
  branch_url: string;
  title: string;
  status: TaskStatus;
  migration: unknown;
  created_by: string;
  initiator_name: string | null;
  initiator_image: string | null;
  chatId?: string | null;
  created_at: string;
  updated_at: string;
  origin?: { kind?: string; source?: string };
  preview?: string | null;
}

interface TaskDetail extends TaskRow {
  chats: { id: string; title: string | null; preview: string | null }[];
  workspace_paths: string[];
}

type ArtifactTab = {
  id: string;
  path: string;
  kind: "text" | "sketch" | "pdf";
  url?: AutomergeUrl;
  src?: string;
  reveal?: { ids: string[] };
  heading?: { text: string };
  line?: number;
  thread?: { threadId: string };
  content?: string;
};

async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (res.ok) return (await res.json()) as T;
  const body = (await res.json().catch(() => null)) as {
    error?: string;
  } | null;
  throw new Error(body?.error ?? `Request failed (${res.status})`);
}

function taskReviewOptions(taskId: string) {
  return queryOptions({
    queryKey: ["task-review", taskId],
    queryFn: async ({ signal }) =>
      jsonOrThrow<TaskReview>(
        await fetch(`/api/tasks/${taskId}/changes`, { signal }),
      ),
    retry: false,
  });
}

function fileKind(path: string): ArtifactTab["kind"] | null {
  const lower = path.toLowerCase();
  if (lower.endsWith(".pdf")) return "pdf";
  if (lower.endsWith(".tldr")) return "sketch";
  if (/\.(md|mdx|ts|tsx|txt|json|yaml|yml|csv)$/.test(lower)) return "text";
  return null;
}

export const Route = createFileRoute("/p/$projectId/workshop")({
  component: WorkshopRoute,
});

const projectRoute = getRouteApi("/p/$projectId");

function WorkshopRoute() {
  const { projectId } = Route.useParams();
  const { indexUrl } = projectRoute.useLoaderData();
  return <WorkshopPage projectId={projectId} indexUrl={indexUrl} />;
}

export function WorkshopPage({
  projectId,
  indexUrl,
  taskId,
  taskView,
  taskFile,
}: {
  projectId: string;
  indexUrl: string;
  taskId?: string;
  taskView?: TaskView;
  taskFile?: string;
}) {
  const queryClient = useQueryClient();
  const [project] = useDocument<Project>(indexUrl as AutomergeUrl, {
    suspense: true,
  });
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [changeCounts, setChangeCounts] = useState<Record<string, number>>({});
  const [activities, setActivities] = useState<Record<string, TaskActivity>>(
    {},
  );
  const refreshRequest = useRef(0);
  const [selected, setSelected] = useState<TaskDetail | null>(null);
  const [creating, setCreating] = useState(false);
  const shellTasks = useMemo(
    () => tasks.map((task) => ({ ...task, activity: activities[task.id] })),
    [tasks, activities],
  );
  useProjectShellTasks(shellTasks);

  const refreshTasks = useCallback(async () => {
    const request = ++refreshRequest.current;
    const res = await fetch(
      `/api/tasks?projectId=${encodeURIComponent(projectId)}`,
    );
    const rows = await jsonOrThrow<TaskRow[]>(res);
    if (request !== refreshRequest.current) return;
    setTasks(rows);
    setSelected((current) => {
      const latest = rows.find((row) => row.id === current?.id);
      return current && latest && current.title !== latest.title
        ? { ...current, title: latest.title }
        : current;
    });
    const snapshots = await Promise.all(
      rows
        .filter((task) => task.status === "open")
        .map(async (task) => {
          const [count, activity] = await Promise.all([
            queryClient
              .fetchQuery(taskReviewOptions(task.id))
              .then((review) => review.changes.length)
              .catch(() => 0),
            task.chatId
              ? fetchTaskActivity(projectId, task.chatId).catch(() => null)
              : Promise.resolve("not_started" as const),
          ]);
          return { id: task.id, count, activity };
        }),
    );
    if (request === refreshRequest.current) {
      setChangeCounts(
        Object.fromEntries(snapshots.map(({ id, count }) => [id, count])),
      );
      setActivities(
        Object.fromEntries(
          snapshots.flatMap(({ id, activity }) =>
            activity ? [[id, activity] as const] : [],
          ),
        ),
      );
    }
  }, [projectId, queryClient]);

  useEffect(() => {
    void refreshTasks().catch((error: unknown) => toast.error(String(error)));
  }, [refreshTasks]);

  useEffect(() => {
    const open = tasks.flatMap((task) =>
      task.status === "open" && task.chatId
        ? [{ id: task.id, chatId: task.chatId }]
        : [],
    );
    if (open.length === 0) return;
    let live = true;
    const timer = window.setInterval(() => {
      if (document.hidden) return;
      void Promise.all(
        open.map(async (task) => ({
          id: task.id,
          activity: await fetchTaskActivity(projectId, task.chatId).catch(
            () => null,
          ),
        })),
      ).then((snapshots) => {
        if (!live) return;
        const settled = snapshots.some(
          ({ id, activity }) =>
            activity === "settled" && activities[id] !== "settled",
        );
        setActivities(
          Object.fromEntries(
            snapshots.flatMap(({ id, activity }) =>
              activity ? [[id, activity] as const] : [],
            ),
          ),
        );
        if (settled) void refreshTasks().catch(() => {});
      });
    }, 5_000);
    return () => {
      live = false;
      window.clearInterval(timer);
    };
  }, [activities, projectId, refreshTasks, tasks]);

  useEffect(() => {
    if (!taskId) {
      setSelected(null);
      return;
    }
    let cancelled = false;
    void fetch(`/api/tasks/${encodeURIComponent(taskId)}`)
      .then(jsonOrThrow<TaskDetail>)
      .then((task) => {
        if (!cancelled) setSelected(task);
      })
      .catch((error: unknown) => {
        if (!cancelled) toast.error(String(error));
      });
    return () => {
      cancelled = true;
    };
  }, [taskId]);

  useEffect(() => {
    document.title =
      taskId && selected?.id === taskId
        ? `${selected.title} · ${project.name} · Bear`
        : `Workshop · ${project.name} · Bear`;
  }, [project.name, selected, taskId]);

  // A conversation starts on main, beside the project; the agent opens a
  // task when the work deserves review (`task` tool), and the view follows.
  const chats = useSharedProjectChats();
  const followConversation = useFollowConversation(projectId);
  const startConversation = async (sent: SentMessage, modelId: string) => {
    const request = sent.text.trim();
    if ((!request && sent.files.length === 0) || creating) return;
    setCreating(true);
    try {
      const chat = await createChat(projectId, modelId);
      chats.adopt(chat);
      followConversation(chat.id, null);
      try {
        await chatCommands(`${projectId}:${chat.id}`, {
          agentClass: "drafter-host",
        }).sendMessage(mintId(), {
          id: mintId(),
          role: "user",
          parts: [
            ...(sent.contextParts ?? []),
            ...(request ? [{ type: "text" as const, text: request }] : []),
            ...sent.files,
          ],
        });
      } catch (error) {
        toast.error("Conversation started. Send your request again in chat.");
        throw error;
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Could not start agent",
      );
    } finally {
      setCreating(false);
    }
  };

  const chooseTask = (id?: string) => {
    if (id) {
      void navigate({
        to: "/p/$projectId/$branch/workbench/$view",
        params: { projectId, branch: id, view: "changes" },
        search: {},
      });
    } else {
      void navigate({
        to: "/p/$projectId/workshop",
        params: { projectId },
        search: {},
      });
    }
  };

  const inSection = (section: WorkshopSection) =>
    tasks.filter(
      (task) =>
        workshopTaskState(task, activities[task.id]).section === section,
    );
  const attention = inSection("attention");
  const workingTasks = inSection("working");
  const openTasks = inSection("open");
  const completedTasks = inSection("completed");

  const taskRow = (task: TaskRow) => {
    const changes = changeCounts[task.id] ?? 0;
    const activity = activities[task.id];
    const state = workshopTaskState(task, activity);
    const isCompleted = state.section === "completed";
    const initiatorName = task.initiator_name || "Former member";
    const externalSource = task.origin?.source?.trim();
    const externalMark = externalSource
      ? externalSource === "Revizto"
        ? "Rz"
        : externalSource.slice(0, 2)
      : null;
    const initiatorInitials = initiatorName
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("");
    return (
      <div key={task.id} role="listitem">
        <Item
          asChild
          size="sm"
          className={
            isCompleted ? "workshop-completed-row" : "workshop-task-row"
          }
        >
          <button onClick={() => chooseTask(task.id)}>
            <span className="workshop-task-status" data-status={state.section}>
              <span aria-hidden="true" />
              {state.label}
            </span>
            <ItemContent className="min-w-0">
              <ItemTitle className="max-w-full truncate" title={task.title}>
                {task.title}
              </ItemTitle>
              {!isCompleted ? (
                <ItemDescription className="truncate text-xs">
                  {task.preview ||
                    `Updated ${new Date(task.updated_at).toLocaleDateString()}`}
                </ItemDescription>
              ) : null}
            </ItemContent>
            <span className="workshop-task-origin">
              {task.origin?.kind === "user" ? (
                <Avatar
                  className="size-6 border border-border"
                  title={initiatorName}
                >
                  <AvatarImage
                    src={task.initiator_image ?? undefined}
                    alt={initiatorName}
                  />
                  <AvatarFallback className="text-[9px]">
                    {initiatorInitials}
                  </AvatarFallback>
                </Avatar>
              ) : (
                <span
                  className="flex size-6 shrink-0 items-center justify-center rounded-md border border-border bg-muted text-[9px] font-semibold text-primary"
                  title={externalSource || "External trigger"}
                >
                  {externalMark ?? <Box className="size-3.5" />}
                </span>
              )}
              <span className="min-w-0">
                <span className="block truncate">
                  {task.origin?.kind === "user"
                    ? initiatorName
                    : externalSource || "External trigger"}
                </span>
                <small>
                  {new Date(task.created_at).toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                  })}
                </small>
              </span>
            </span>
            <ItemActions className="justify-end gap-2 text-xs text-muted-foreground">
              {!isCompleted && changes > 0
                ? `${changes} ${changes === 1 ? "change" : "changes"}`
                : null}
              <ChevronRight className="size-4 shrink-0" />
            </ItemActions>
          </button>
        </Item>
      </div>
    );
  };

  return (
    <>
      {taskId && selected?.id === taskId ? (
        <TaskBranch
          key={taskId}
          projectId={projectId}
          task={selected}
          taskView={taskView}
          taskFile={taskFile}
          onTaskChanged={() => {
            void refreshTasks();
            void fetch(`/api/tasks/${taskId}`)
              .then(jsonOrThrow<TaskDetail>)
              .then(setSelected);
          }}
        />
      ) : (
        <main className="min-h-0 flex-1 overflow-y-auto px-5 py-8 md:px-[54px] md:pt-14">
          <div className="mx-auto max-w-[1152px]">
            <h1 className="workshop-title mb-5 text-[27px] font-bold tracking-tight">
              What should we work on?
            </h1>
            <WorkshopPrompt
              projectId={projectId}
              indexUrl={indexUrl as AutomergeUrl}
              creating={creating}
              onStart={(message, modelId) =>
                void startConversation(message, modelId)
              }
            />
            {attention.length > 0 ? (
              <section className="mt-9">
                <h2 className="workshop-section-heading">
                  <CircleDot className="size-3.5" /> Needs your attention
                  <span>{attention.length}</span>
                </h2>
                <ItemGroup className="workshop-task-list overflow-hidden rounded-lg border bg-card">
                  {attention.map(taskRow)}
                </ItemGroup>
              </section>
            ) : null}
            {workingTasks.length > 0 ? (
              <section className="mt-9">
                <h2 className="workshop-section-heading">
                  Working in the background
                  <span>{workingTasks.length}</span>
                </h2>
                <ItemGroup className="workshop-task-list workshop-working-list border-y">
                  {workingTasks.map(taskRow)}
                </ItemGroup>
              </section>
            ) : null}
            {openTasks.length > 0 ? (
              <section className="mt-9">
                <h2 className="workshop-section-heading">
                  <CircleDot className="size-3.5" /> Open tasks
                  <span>{openTasks.length}</span>
                </h2>
                <ItemGroup className="workshop-task-list overflow-hidden rounded-lg border bg-card">
                  {openTasks.map(taskRow)}
                </ItemGroup>
              </section>
            ) : null}
            {completedTasks.length > 0 ? (
              <section className="mt-9">
                <h2 className="mb-4 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Completed tasks
                </h2>
                <ItemGroup className="workshop-task-list workshop-completed-list border-t">
                  {completedTasks.map(taskRow)}
                </ItemGroup>
              </section>
            ) : null}
          </div>
        </main>
      )}
    </>
  );
}

function TaskBranch({
  projectId,
  task,
  taskView,
  taskFile,
  onTaskChanged,
}: {
  projectId: string;
  task: TaskDetail;
  taskView?: TaskView;
  taskFile?: string;
  onTaskChanged: () => void;
}) {
  const [project] = useDocument<Project>(task.branch_url as AutomergeUrl, {
    suspense: true,
  });
  return (
    <BearSandboxProvider
      project={project}
      projectId={projectId}
      indexUrl={task.branch_url as AutomergeUrl}
      taskId={task.id}
    >
      <JigsProvider projectId={projectId}>
        <TaskSurface
          key={task.id}
          projectId={projectId}
          project={project}
          task={task}
          taskView={taskView}
          taskFile={taskFile}
          onTaskChanged={onTaskChanged}
        />
      </JigsProvider>
    </BearSandboxProvider>
  );
}

/** A view the workbench shows beside the chat, other than an open file. */
export type TaskView = "model" | "checks" | "context";
const PANELS = ["changes", "model", "checks", "context"];
const isPanel = (view: string) => PANELS.includes(view);

/**
 * HACKY: the model a task shows is its project's default entry, found the way
 * the explorer finds its landing model, again whenever the tree changes (the
 * agent may write the model after the task opens). The agent presenting the work will
 * name the file instead. With no entry there is nothing to mark up; the model
 * pane says why.
 */
function useTaskModelFile(playground: HostPlayground): string | undefined {
  const [file, setFile] = useState<string>();
  const tree = useTreeVersion(playground);
  useEffect(() => {
    let live = true;
    void resolveModelEntry(async (path) => {
      const found = await playground.open(path);
      try {
        return found.kind === "text" ? await found.read() : "";
      } finally {
        await found.close();
      }
    }).then(
      (entry) => {
        if (live) setFile(entry);
      },
      () => {
        if (live) setFile(undefined);
      },
    );
    return () => {
      live = false;
    };
  }, [playground, tree]);
  return file;
}

function TaskSurface({
  projectId,
  project,
  task,
  taskView,
  taskFile,
  onTaskChanged,
}: {
  projectId: string;
  project: Project;
  task: TaskDetail;
  taskView?: TaskView;
  taskFile?: string;
  onTaskChanged: () => void;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const repo = useContext(RepoContext);
  const { playground, setWorkspacePresenter } = useBearSandbox();
  const entries = useTreeEntries(playground);
  const chats = useSharedProjectChats();
  const followConversation = useFollowConversation(projectId);
  const checkpoints = useCheckpointRail({
    repo,
    indexUrl: task.branch_url as AutomergeUrl,
    checkpointsDocUrl: project.checkpointsDocUrl,
    files: project.files,
    restoreMessage: (checkpoint) =>
      `Restored checkpoint: ${checkpoint.description}`,
  });
  const isLegacyChat = task.origin?.source === "legacy_chat";
  const workbenchRoute = getRouteApi("/p/$projectId/$branch/workbench/$view");
  const { cam, plane, thread } = workbenchRoute.useSearch();
  const [contextReads, setContextReads] = useState<TaskRead[]>([]);
  const [contextLoaded, setContextLoaded] = useState(false);
  const contextSignature = useRef("");
  const onFeedMessages = useCallback((messages: UIMessage[]) => {
    setContextLoaded(true);
    const next = taskReads(messages);
    const signature = JSON.stringify(next);
    if (signature === contextSignature.current) return;
    contextSignature.current = signature;
    setContextReads(next);
  }, []);
  const [tabs, setTabs] = useState<ArtifactTab[]>([]);
  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;
  const workspacePaths = useRef(task.workspace_paths);
  const workspaceSave = useRef<Promise<void>>(Promise.resolve());
  const active = taskFile ?? taskView ?? "changes";
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [listFocus, setListFocus] = useState<{ threadId: string }>();
  const modelFile = useTaskModelFile(playground);
  const checkFiles = useMemo(
    () =>
      (entries ?? [])
        .filter((entry) => !entry.isDirectory && isCheckFile(entry.path))
        .map((entry) => ({
          path: entry.path,
          mtime: hasStats(entry.inode) ? entry.inode.mtime : null,
        }))
        .sort((a, b) => a.path.localeCompare(b.path)),
    [entries],
  );
  const checks = useTaskChecks(checkFiles);
  const failing = failureCount(checks.state);
  const navigateMarkup = useCallback(
    (view: MarkupView, mode: "push" | "replace") =>
      void navigate({
        to: "/p/$projectId/$branch/workbench/$view",
        params: { projectId, branch: task.id, view: "model" },
        search: (previous) => ({
          ...previous,
          file: undefined,
          cam: view.cam,
          plane: view.plane,
          thread: view.thread,
        }),
        replace: mode === "replace",
      }),
    [navigate, projectId, task.id],
  );
  // Markup while the model is what the task shows (a hidden view still
  // reports its camera), and while the task is open to edits.
  const markupHost = useMemo<ModelMarkupHost | undefined>(
    () =>
      modelFile && active === "model" && task.status === "open"
        ? {
            file: modelFile,
            indexUrl: task.branch_url as AutomergeUrl,
            cam,
            plane,
            thread,
            navigate: navigateMarkup,
          }
        : undefined,
    [
      active,
      cam,
      thread,
      modelFile,
      navigateMarkup,
      plane,
      task.branch_url,
      task.status,
    ],
  );
  const setActive = useCallback(
    (next: string) => {
      void navigate({
        to: "/p/$projectId/$branch/workbench/$view",
        params: {
          projectId,
          branch: task.id,
          view: isPanel(next) ? next : "document",
        },
        search: (previous) => ({
          ...previous,
          file: isPanel(next) ? undefined : next,
        }),
      });
    },
    [navigate, projectId, task.id],
  );
  const [review, setReview] = useState<TaskReview | null>(null);
  const [execution, setExecution] = useState<Execution | null>(null);
  useEffect(() => {
    setExecution(task.migration ? executionSchema.parse(task.migration) : null);
  }, [task.migration]);
  const [preview, setPreview] = useState<BranchChangePreview | null>(null);
  const [previewPath, setPreviewPath] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const previewRequest = useRef(0);
  const reviewRequest = useRef(0);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [confirmApply, setConfirmApply] = useState(false);
  const previousStatus = useRef(task.status);
  useEffect(() => {
    const leftOpen =
      previousStatus.current === "open" && task.status !== "open";
    previousStatus.current = task.status;
    if (leftOpen) {
      setTabs([]);
      setActive("changes");
    }
  }, [setActive, task.status]);
  const chatId = task.chats[0]?.id;
  // A comment addressed to Bear goes to this task's conversation, or opens a
  // new one in the task when that conversation has moved on.
  const askBear = useAskBearDispatch({
    projectId,
    taskId: task.id,
    chatId: chatId ?? null,
    open: (id) => followConversation(id, task.id),
  });
  const activity = useTaskActivity(projectId, chatId);
  const activeTab = tabs.find((tab) => tab.id === active);
  const baseHead = isBranchedProject(project)
    ? project.branch.source.heads[0]?.slice(0, 8)
    : undefined;

  const saveWorkspace = useCallback(
    (paths: string[]) => {
      workspacePaths.current = paths;
      const pending = workspaceSave.current
        .catch(() => {})
        .then(async () => {
          const current = workspacePaths.current;
          const saved = await jsonOrThrow<{ workspace_paths: string[] }>(
            await fetch(`/api/tasks/${task.id}/workspace`, {
              method: "PUT",
              headers: { "content-type": "application/json" },
              body: JSON.stringify({ paths: current }),
            }),
          );
          if (workspacePaths.current === current) {
            workspacePaths.current = saved.workspace_paths;
          }
        });
      workspaceSave.current = pending;
      return pending.catch((error: unknown) => {
        toast.error(
          error instanceof Error ? error.message : "Could not save workspace",
        );
      });
    },
    [task.id],
  );

  const loadReview = useMemo(
    () =>
      coalescedRefresh(async () =>
        queryClient.fetchQuery(taskReviewOptions(task.id)),
      ),
    [queryClient, task.id],
  );
  const refreshReview = useCallback(async () => {
    const request = ++reviewRequest.current;
    try {
      const next = await loadReview();
      if (request !== reviewRequest.current) return;
      previewRequest.current++;
      setReview(next);
      setPreview(null);
      setPreviewPath(null);
      setReviewError(null);
    } catch (error) {
      if (request === reviewRequest.current) {
        setReviewError(error instanceof Error ? error.message : String(error));
      }
    }
  }, [loadReview]);

  const openPreview = async (path: string | null) => {
    const request = ++previewRequest.current;
    setPreviewPath(path);
    setPreview(null);
    setPreviewError(null);
    if (path === null) return;
    try {
      const res = await fetch(
        `/api/tasks/${task.id}/changes/preview?path=${encodeURIComponent(path)}`,
      );
      const next = await jsonOrThrow<BranchChangePreview>(res);
      if (request === previewRequest.current) setPreview(next);
    } catch (error) {
      if (request === previewRequest.current) {
        setPreviewError(error instanceof Error ? error.message : String(error));
      }
    }
  };
  useEffect(() => {
    if (!isLegacyChat) void refreshReview();
  }, [activity, entries, isLegacyChat, refreshReview]);

  const openArtifact = useCallback(
    async (
      path: string,
      reveal?: {
        heading?: string;
        line?: number;
        shapes?: string[];
        threadId?: string;
      },
      persist = true,
      focus = true,
    ): Promise<string> => {
      const found = await playground.resolve(path);
      if (!found) return `${path} was not found`;
      const kind = fileKind(found.path);
      if (!kind) return `${path} has no workspace viewer`;
      if (
        task.status === "open" &&
        found.inode.version?.kind === "automerge" &&
        !isMountInode(found.inode)
      ) {
        invariant(repo, "Automerge repo is not available");
        await forkBranchFile(repo, task.branch_url as AutomergeUrl, found.path);
      }
      const current = await playground.resolve(found.path);
      invariant(current, `File disappeared: ${path}`);
      invariant(repo, "Automerge repo is not available");
      let content: string | undefined;
      if (task.status !== "open" && kind === "text") {
        const file = await playground.open(found.path);
        if (file.kind === "text") content = await file.read();
        await file.close();
      }
      const tab: ArtifactTab = {
        id: found.path,
        path: found.path,
        kind,
        ...(content !== undefined ? { content } : {}),
        ...(current.inode.uri.startsWith("automerge:")
          ? { url: current.inode.uri as AutomergeUrl }
          : {}),
        ...(kind === "pdf" ? { src: await blobSrc(repo, current.inode) } : {}),
        ...(reveal?.shapes ? { reveal: { ids: reveal.shapes } } : {}),
        ...(reveal?.heading ? { heading: { text: reveal.heading } } : {}),
        ...(reveal?.line ? { line: reveal.line } : {}),
        ...(reveal?.threadId ? { thread: { threadId: reveal.threadId } } : {}),
      };
      setTabs((currentTabs) => [
        ...currentTabs.filter((item) => item.id !== tab.id),
        tab,
      ]);
      if (focus) setActive(tab.id);
      if (
        persist &&
        task.status === "open" &&
        !workspacePaths.current.includes(found.path)
      ) {
        await saveWorkspace([...workspacePaths.current, found.path]);
      }
      return `${path} open`;
    },
    [playground, repo, saveWorkspace, setActive, task.branch_url, task.status],
  );

  useEffect(() => {
    for (const path of task.workspace_paths) {
      void openArtifact(path, undefined, false, false);
    }
    // The task identity owns its saved working set. Reopening it is the only
    // hydration event; later tab changes are handled directly below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.id]);

  const routedFileRequest = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!taskFile) {
      routedFileRequest.current = undefined;
      return;
    }
    if (routedFileRequest.current === taskFile) return;
    routedFileRequest.current = taskFile;
    void playground
      .resolve(taskFile)
      .then(async (found) => {
        if (routedFileRequest.current !== taskFile || !found) return;
        if (found.path !== taskFile) {
          setActive(found.path);
          return;
        }
        if (!tabsRef.current.some((tab) => tab.path === found.path)) {
          await openArtifact(found.path, undefined, false, false);
        }
      })
      .catch((error: unknown) => {
        toast.error(error instanceof Error ? error.message : String(error));
      });
  }, [openArtifact, playground, setActive, taskFile]);

  const closeArtifact = (path: string) => {
    setTabs((items) => items.filter((item) => item.id !== path));
    if (active === path) setActive("changes");
    if (task.status !== "open") return;
    void saveWorkspace(workspacePaths.current.filter((item) => item !== path));
  };

  const presentArtifacts = useCallback(
    async (paths: string[]) => {
      if (task.status !== "open") throw new Error("Task is no longer open");
      if (paths.length > 12 || new Set(paths).size !== paths.length) {
        throw new Error("Present up to 12 distinct artifacts");
      }
      for (const path of paths) {
        if (!fileKind(path) || !(await playground.resolve(path))) {
          throw new Error(`File has no workspace viewer: ${path}`);
        }
      }
      await saveWorkspace(paths);
      for (const path of paths)
        await openArtifact(path, undefined, false, false);
      setTabs((items) => items.filter((item) => paths.includes(item.path)));
      setActive(
        isPanel(active) || paths.includes(active)
          ? active
          : (paths[0] ?? "changes"),
      );
    },
    [active, openArtifact, playground, saveWorkspace, setActive, task.status],
  );
  useEffect(() => {
    setWorkspacePresenter(presentArtifacts);
    return () => setWorkspacePresenter(null);
  }, [presentArtifacts, setWorkspacePresenter]);

  const onShow = useCallback(
    async (input: ShowInput) => {
      if (input.kind === "model") {
        setActive("model");
        return "Model open. Use the model view to inspect the requested elements.";
      }
      if (input.kind === "sketch")
        return openArtifact(input.path, { shapes: input.shapes });
      if (input.kind === "comment") {
        invariant(repo, "Automerge repo is not available");
        const found = await playground.resolve(input.path);
        const handle = found?.inode.uri.startsWith("automerge:")
          ? await repo.find<AnnotatedDoc>(found.inode.uri as AutomergeUrl)
          : undefined;
        await handle?.whenReady();
        const doc = handle?.doc();
        const thread = doc?.[`comment:${input.threadId}`];
        if (!thread) {
          return `${input.path} has no comment thread ${input.threadId}`;
        }
        // A redline opens on its plane, square-on, with its card open.
        const markup = markupViewOf(doc, input.threadId);
        if (markup) {
          navigateMarkup(markup, "push");
          return `${input.path} open on its markup plane, at thread ${input.threadId}`;
        }
        // A thread whose quoted text is gone has nowhere to open in the text,
        // so it opens in the file's comment list instead.
        const anchored = textAnnotations(doc).some(
          ({ annotation, range }) =>
            annotation.id === input.threadId && range !== null,
        );
        if (anchored) {
          return openArtifact(input.path, { threadId: input.threadId });
        }
        setCommentsOpen(true);
        setListFocus({ threadId: input.threadId });
        return openArtifact(input.path);
      }
      return openArtifact(input.path, {
        heading: input.heading,
        line: input.line,
      });
    },
    [openArtifact, setActive, playground, repo],
  );

  const runApprovedMigration = async () => {
    try {
      const res = await fetch(`/api/agents/migrations/${task.id}`, {
        method: "POST",
      });
      setExecution(executionSchema.parse(await jsonOrThrow(res)));
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "External outcome is not confirmed",
      );
    } finally {
      onTaskChanged();
    }
  };

  const attachExample = async () => {
    try {
      await jsonOrThrow(
        await fetch(`/api/tasks/${task.id}/migration-example`, {
          method: "POST",
        }),
      );
      await refreshReview();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Could not attach example",
      );
    }
  };

  const reconcile = async () => {
    const res = await fetch(`/api/tasks/${task.id}/reconcile-migration`, {
      method: "POST",
    });
    setExecution(executionSchema.parse(await jsonOrThrow(res)));
    onTaskChanged();
  };

  const apply = async () => {
    if (!review || applying) return;
    setApplying(true);
    try {
      if (
        !chatId ||
        !canReviewTask(await fetchTaskActivity(projectId, chatId))
      ) {
        throw new Error("Wait for the agent to finish before applying changes");
      }
      const res = await fetch(`/api/tasks/${task.id}/apply`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(review),
      });
      await jsonOrThrow(res);
      toast.success("Changes applied to main");
      onTaskChanged();
      if (review.migration) await runApprovedMigration();
      await refreshReview();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Could not apply changes",
      );
      await refreshReview();
    } finally {
      setConfirmApply(false);
      setApplying(false);
    }
  };

  const complete = async () => {
    if (completing) return;
    setCompleting(true);
    try {
      if (
        !chatId ||
        (await fetchTaskActivity(projectId, chatId)) !== "settled"
      ) {
        throw new Error(
          "Wait for the agent to finish before completing the task",
        );
      }
      await jsonOrThrow(
        await fetch(
          `/api/agents/drafter-host/${projectId}:${chatId}/complete-task`,
          { method: "POST" },
        ),
      );
      toast.success("Task completed");
      onTaskChanged();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Could not complete task",
      );
      await refreshReview();
    } finally {
      setCompleting(false);
    }
  };

  return (
    <AskBearProvider value={task.status === "open" ? askBear : null}>
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex min-h-14 shrink-0 items-center gap-3 border-b px-3 py-2">
          <Link
            to="/p/$projectId/workshop"
            params={{ projectId }}
            search={{}}
            aria-label="Back to Workshop"
            className="rounded p-1 hover:bg-accent"
          >
            <ArrowLeft className="size-4" />
          </Link>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-sm font-semibold">{task.title}</h1>
            <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              {isLegacyChat ? (
                <span>Past conversation</span>
              ) : (
                <>
                  <GitBranch className="size-3" />
                  <span>task/{task.id.slice(-8).toLowerCase()}</span>
                  {baseHead ? <span>· from main {baseHead}</span> : null}
                </>
              )}
              <span>· {workshopTaskState(task, activity).label}</span>
            </p>
          </div>
          {task.status === "open" ? (
            <Link
              to={
                activeTab
                  ? "/p/$projectId/$branch/explorer/files/$"
                  : "/p/$projectId/$branch/explorer"
              }
              params={{
                projectId,
                branch: task.id,
                _splat: activeTab?.path ?? "",
              }}
              search={{}}
              className="shrink-0 text-xs hover:text-primary"
            >
              Open in Explorer
            </Link>
          ) : null}
          {task.status === "open" &&
          canReviewTask(activity) &&
          (review?.changes.length || review?.migration) ? (
            <Button
              size="sm"
              onClick={() => {
                setActive("changes");
                setConfirmApply(true);
              }}
            >
              <GitMerge className="size-3.5" /> Review & apply
            </Button>
          ) : task.status === "open" &&
            activity === "settled" &&
            review &&
            review.changes.length === 0 &&
            !review.migration ? (
            <Button
              size="sm"
              disabled={completing}
              onClick={() => void complete()}
            >
              {completing ? "Completing…" : "Complete task"}
            </Button>
          ) : null}
        </div>
        <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
          {!isLegacyChat ? (
            <section className="flex min-h-0 min-w-0 flex-1 flex-col bg-card">
              <div className="flex h-10 shrink-0 items-center gap-1 overflow-x-auto border-b px-2">
                <button
                  className={cn(
                    "flex items-center gap-1 rounded px-2 py-1 text-xs font-medium",
                    active === "changes"
                      ? "bg-accent"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                  onClick={() => {
                    setActive("changes");
                    void refreshReview();
                  }}
                >
                  <GitMerge className="size-3.5" /> Changes
                </button>
                <button
                  className={cn(
                    "flex items-center gap-1 rounded px-2 py-1 text-xs font-medium",
                    active === "model"
                      ? "bg-accent"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                  onClick={() => setActive("model")}
                >
                  <Box className="size-3.5" /> Model
                </button>
                <button
                  className={cn(
                    "flex items-center gap-1 rounded px-2 py-1 text-xs font-medium",
                    active === "checks"
                      ? "bg-accent"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                  onClick={() => setActive("checks")}
                >
                  <ListChecks className="size-3.5" /> Checks
                  {failing > 0 && (
                    <span
                      className="ml-0.5 rounded-full bg-destructive px-1.5 text-[10px] leading-4 font-semibold text-white"
                      aria-label={
                        failing === 1
                          ? "1 failing check"
                          : `${failing} failing checks`
                      }
                    >
                      {failing}
                    </span>
                  )}
                </button>
                <button
                  className={cn(
                    "flex items-center gap-1 rounded px-2 py-1 text-xs font-medium",
                    active === "context"
                      ? "bg-accent"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                  onClick={() => setActive("context")}
                >
                  <BookOpen className="size-3.5" /> Context
                </button>
                <div className="mx-1 h-4 w-px bg-border" />
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    className={cn(
                      "group flex max-w-44 shrink-0 items-center gap-1 rounded px-2 py-1 text-xs",
                      active === tab.id
                        ? "bg-accent font-medium"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                    onClick={() => setActive(tab.id)}
                  >
                    <span className="truncate">
                      {tab.path.split("/").at(-1)}
                    </span>
                    <X
                      className="size-3 opacity-0 group-hover:opacity-100"
                      onClick={(event) => {
                        event.stopPropagation();
                        closeArtifact(tab.id);
                      }}
                    />
                  </button>
                ))}
                {activeTab?.kind === "text" &&
                activeTab.url &&
                task.status === "open" ? (
                  <div className="ml-auto shrink-0 pl-2">
                    <CommentsButton
                      url={activeTab.url}
                      open={commentsOpen}
                      onToggle={() => setCommentsOpen((open) => !open)}
                    />
                  </div>
                ) : null}
              </div>
              <div className="min-h-0 flex-1 overflow-hidden">
                <div
                  className={cn(
                    "h-full overflow-y-auto p-4",
                    active !== "changes" && "hidden",
                  )}
                >
                  {execution && (
                    <div className="mb-4">
                      <MigrationStatus
                        execution={execution}
                        run={() => void runApprovedMigration()}
                        reconcile={reconcile}
                      />
                    </div>
                  )}
                  {reviewError ? (
                    <p role="alert" className="mb-4 text-sm text-destructive">
                      {reviewError}
                    </p>
                  ) : null}
                  {review ? (
                    <>
                      <div className="px-4 pb-4 pt-2">
                        <h2 className="text-sm font-semibold">
                          {task.status === "open"
                            ? "Proposed changes"
                            : "Task changes"}
                        </h2>
                      </div>
                      <TaskChanges
                        review={review}
                        previewPath={previewPath}
                        preview={preview}
                        previewError={previewError}
                        onPreview={(path) => void openPreview(path)}
                      >
                        {!execution && review.migration ? (
                          <MigrationReview
                            taskId={task.id}
                            artifact={review.migration}
                          />
                        ) : null}
                      </TaskChanges>
                      {task.status === "open" && !review.migration && (
                        <details className="mt-4 px-4 text-xs text-muted-foreground">
                          <summary className="cursor-pointer">
                            Simulation tools
                          </summary>
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-3"
                            onClick={() => void attachExample()}
                          >
                            Attach simulated action
                          </Button>
                        </details>
                      )}
                    </>
                  ) : !reviewError ? (
                    <p
                      role="status"
                      className="p-4 text-sm text-muted-foreground"
                    >
                      Loading changes…
                    </p>
                  ) : null}
                </div>
                <div
                  className={cn(
                    "h-full overflow-y-auto py-4",
                    active !== "checks" && "hidden",
                  )}
                >
                  <TaskChecks
                    state={checks.state}
                    run={() => void checks.run()}
                  />
                </div>
                <div
                  className={cn(
                    "flex h-full flex-col",
                    active !== "context" && "hidden",
                  )}
                >
                  <TaskContext
                    projectId={projectId}
                    taskId={task.id}
                    taskOpen={task.status === "open"}
                    legacyChat={isLegacyChat}
                    reads={contextReads}
                    loaded={contextLoaded || !chatId}
                  />
                </div>
                <div className={cn("h-full", active !== "model" && "hidden")}>
                  <BearModelPane
                    onOpenPath={(path) => {
                      void openArtifact(path);
                    }}
                    markup={markupHost}
                  />
                </div>
                {tabs.map((tab) => (
                  <div
                    key={tab.id}
                    className={cn(
                      "relative h-full",
                      active !== tab.id && "hidden",
                    )}
                  >
                    {commentsOpen && active === tab.id && tab.url ? (
                      <FileComments
                        url={tab.url}
                        path={tab.path}
                        focus={listFocus}
                        onClose={() => setCommentsOpen(false)}
                        onReveal={(threadId) =>
                          setTabs((current) =>
                            current.map((item) =>
                              item.id === tab.id
                                ? { ...item, thread: { threadId } }
                                : item,
                            ),
                          )
                        }
                      />
                    ) : null}
                    {task.status !== "open" && tab.kind === "text" ? (
                      <pre className="h-full overflow-auto p-4 font-mono text-xs leading-5 whitespace-pre-wrap">
                        {tab.content ?? "Preview unavailable"}
                      </pre>
                    ) : task.status !== "open" && tab.kind === "sketch" ? (
                      <div className="p-4 text-sm text-muted-foreground">
                        Sketch editing is closed for this task.
                      </div>
                    ) : tab.kind === "text" ? (
                      <EditorPane
                        paneId={tab.id}
                        filename={tab.path}
                        url={tab.url}
                        revealHeading={tab.heading}
                        revealThread={tab.thread}
                        revealSelection={
                          tab.line
                            ? { startLineNumber: tab.line, startColumn: 1 }
                            : undefined
                        }
                        onOpenPath={(path) => {
                          void openArtifact(path);
                        }}
                      />
                    ) : tab.kind === "sketch" ? (
                      <SketchPane
                        paneId={tab.id}
                        filename={tab.path}
                        url={tab.url}
                        reveal={tab.reveal}
                      />
                    ) : (
                      <PdfPane
                        paneId={tab.id}
                        filename={tab.path}
                        src={tab.src}
                      />
                    )}
                  </div>
                ))}
              </div>
            </section>
          ) : null}
          <section
            className={cn(
              "flex min-h-[280px] min-w-0 flex-1 flex-col bg-card",
              isLegacyChat
                ? "lg:min-h-0"
                : "border-t lg:min-h-0 lg:w-[360px] lg:flex-none lg:border-t-0 lg:border-l xl:w-[390px]",
            )}
          >
            {chatId ? (
              <BuilderPane
                projectId={projectId}
                taskId={task.id}
                showHeader={false}
                composerReplacement={
                  task.status !== "open" ? (
                    <ClosedTaskActions
                      task={task}
                      chatId={chatId}
                      onMoved={chats.refresh}
                    />
                  ) : undefined
                }
                project={project}
                indexUrl={task.branch_url as AutomergeUrl}
                chatId={chatId}
                chats={chats}
                checkpoints={checkpoints}
                activeFile={activeTab?.path}
                onShow={onShow}
                onFeedMessages={onFeedMessages}
                onScopeMove={followConversation}
              />
            ) : (
              <div className="p-4 text-sm text-muted-foreground">
                No chat attached to this task.
              </div>
            )}
          </section>
        </div>
        <AlertDialog
          open={confirmApply}
          onOpenChange={(open) => {
            if (!applying) setConfirmApply(open);
          }}
        >
          <AlertDialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
            <AlertDialogHeader>
              <AlertDialogTitle>Review & apply</AlertDialogTitle>
              <AlertDialogDescription>
                {review?.migration
                  ? "Apply these changes to the main project and run the external action below."
                  : "Apply these changes to the main project."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            {review && (
              <TaskChanges
                review={review}
                previewPath={previewPath}
                preview={preview}
                previewError={previewError}
                onPreview={(path) => void openPreview(path)}
              >
                {review.migration ? (
                  <MigrationReview
                    taskId={task.id}
                    artifact={review.migration}
                  />
                ) : null}
              </TaskChanges>
            )}
            {review?.migration && (
              <p className="text-sm text-muted-foreground">
                The external action runs after the Bear changes are applied. If
                it fails, the Bear changes remain applied.
              </p>
            )}
            <AlertDialogFooter>
              <AlertDialogCancel disabled={applying}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                disabled={applying}
                aria-busy={applying}
                onClick={(event) => {
                  event.preventDefault();
                  void apply();
                }}
              >
                {applying && (
                  <Loader2
                    data-icon="inline-start"
                    className="animate-spin [animation-duration:3s] motion-reduce:animate-none"
                    aria-hidden
                  />
                )}
                {applying
                  ? "Applying…"
                  : review?.migration
                    ? "Approve & run action"
                    : "Apply changes"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AskBearProvider>
  );
}

/**
 * A closed task stays history. Its conversation goes on from here: back on
 * main, or in a new task linked to this one. Neither reopens it.
 */
function ClosedTaskActions({
  task,
  chatId,
  onMoved,
}: {
  task: TaskDetail;
  chatId: string;
  onMoved: () => void;
}) {
  const [moving, setMoving] = useState(false);
  const move = async (
    target: { to: "main" } | { to: "new"; title: string; requestId: string },
  ) => {
    setMoving(true);
    try {
      await jsonOrThrow(
        await fetch(`/api/chats/${chatId}/scope`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(target),
        }),
      );
      onMoved();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : String(error));
      setMoving(false);
    }
  };
  return (
    <div className="flex flex-col gap-2 border-t px-4 py-3 text-xs text-muted-foreground">
      <span>
        {task.status === "applied"
          ? "This task was applied and stays as history."
          : "This task is closed and stays as history."}
      </span>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={moving}
          onClick={() => void move({ to: "main" })}
        >
          Continue on main
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={moving}
          onClick={() =>
            void move({
              to: "new",
              title: `Follow-up: ${task.title}`.slice(0, 200),
              requestId: crypto.randomUUID(),
            })
          }
        >
          Follow up in a new task
        </Button>
      </div>
    </div>
  );
}
